import React, { useMemo } from 'react';
import * as THREE from 'three';
import { BeveledPanel } from '../Equipment/FabProcessTool/BeveledPanel';
import { getStockerMaterials } from './StockerMaterials';
import type { StockerDimensions } from './stockerTypes';
import type { StockerLoadPortKind } from './StockerLoadPortBank';
import { useRailElevation } from '../../railElevationContext';
import { FabAmhsLifter3D } from '../Lifter/FabAmhsLifter3D';

export type StockerExternalLoadPortProps = {
  dims: StockerDimensions;
  ports: Array<{ kind: StockerLoadPortKind; x: number; z: number }>;
  dockW: number;
  dockD: number;
  occupiedPorts?: Partial<Record<StockerLoadPortKind, boolean>>;
  activePortKind?: StockerLoadPortKind | null;
  isTransferring?: boolean;
  transferProgress01?: number;
  transferOperation?: 'PICKUP' | 'DROP' | 'TRANSFER';
  /** While transferring, whether the lifter carriage (not the deck) holds the FOUP. */
  lifterFoupPresent?: boolean;
  opacity?: number;
};

/**
 * Dedicated OHT Vertical Lifter Transfer System at Stocker External Load Ports.
 * Features a clean unified 4-pillar outer perimeter frame with light-black transparent smoked glass,
 * eliminating cluttered multiple bars and matching cleanroom equipment aesthetics.
 */
export const StockerExternalLoadPort: React.FC<StockerExternalLoadPortProps> = ({
  dims,
  ports,
  dockW,
  dockD,
  occupiedPorts = {},
  activePortKind,
  isTransferring = false,
  transferProgress01 = 0,
  transferOperation = 'PICKUP',
  lifterFoupPresent = false,
  opacity = 1,
}) => {
  const mats = useMemo(() => getStockerMaterials(opacity), [opacity]);
  const { baseH, loadPortDeckY, frameT } = dims;
  const footH = Math.max(baseH, frameT * 2.4);
  const sceneRailElevation = useRailElevation();

  const r = Math.min(dims.width, dims.depth) * 0.01;
  const nestW = Math.max(dockW * 1.12, 1.15);
  const nestD = Math.max(dockD * 0.95, 0.95);
  const padH = Math.max(0.04, loadPortDeckY * 0.045);

  const TRACK_Y = Math.max(30.0, sceneRailElevation) + 1.2;
  const totalD = nestD + 0.4;
  const halfTD = totalD * 0.5;
  const lifterSideShift = halfTD; // Lifter front outer face sits directly on the track line with 0 space

  // Unified Frame Bounds (Enclosing all ports with exactly 4 outer corner rods)
  const xs = ports.length > 0 ? ports.map((p) => p.x) : [0];
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const totalW = maxX - minX + nestW + 0.6;
  const centerX = (minX + maxX) / 2;
  const lifterZ = (ports[0]?.z ?? 0) + lifterSideShift;
  const shaftH = TRACK_Y - loadPortDeckY;
  const shaftMidY = loadPortDeckY + shaftH * 0.5;
  const postSize = 0.32;
  const halfTW = totalW * 0.5;

  // Smoked Light-Black Transparent Physical Glass
  const smokedGlassMat = useMemo(
    () =>
      new THREE.MeshPhysicalMaterial({
        color: '#0F172A',
        transmission: 0.82,
        opacity: 0.42,
        transparent: true,
        roughness: 0.12,
        metalness: 0.18,
        ior: 1.5,
      }),
    []
  );

  const silverFrameMat = useMemo(
    () =>
      new THREE.MeshStandardMaterial({
        color: '#CBD5E1',
        metalness: 0.82,
        roughness: 0.22,
        envMapIntensity: 1.3,
        transparent: opacity < 1,
        opacity,
      }),
    [opacity]
  );

  return (
    <group name="StockerExternalLoadPort">
      {/* ── 1. UNIFIED 4-CORNER OUTER FRAME ENCLOSURE WITH SMOKED GLASS (NO SPACE TO TRACK) ── */}
      <group position={[centerX, 0, lifterZ]}>
        {/* 4 Outer Corner Aluminum Columns (Floor to Track) */}
        {[-1, 1].flatMap((sx) =>
          [-1, 1].map((sz) => (
            <mesh
              key={`stocker-lifter-col-${sx}-${sz}`}
              position={[sx * (halfTW - postSize * 0.5), shaftMidY, sz * (halfTD - postSize * 0.5)]}
              material={silverFrameMat}
              castShadow
              receiveShadow
            >
              <boxGeometry args={[postSize, shaftH, postSize]} />
            </mesh>
          ))
        )}

        {/* Center Vertical Structural Dividing Pillars (Front & Rear) */}
        <mesh
          position={[0, shaftMidY, -halfTD + postSize * 0.5]}
          material={silverFrameMat}
          castShadow
          receiveShadow
        >
          <boxGeometry args={[postSize, shaftH, postSize]} />
        </mesh>
        <mesh
          position={[0, shaftMidY, halfTD - postSize * 0.5]}
          material={silverFrameMat}
          castShadow
          receiveShadow
        >
          <boxGeometry args={[postSize, shaftH, postSize]} />
        </mesh>

        {/* Lower Perimeter Frame Bezel */}
        <mesh position={[0, loadPortDeckY + postSize * 0.5, -halfTD + postSize * 0.5]} material={silverFrameMat}>
          <boxGeometry args={[totalW, postSize, postSize]} />
        </mesh>
        <mesh position={[0, loadPortDeckY + postSize * 0.5, halfTD - postSize * 0.5]} material={silverFrameMat}>
          <boxGeometry args={[totalW, postSize, postSize]} />
        </mesh>
        <mesh position={[-halfTW + postSize * 0.5, loadPortDeckY + postSize * 0.5, 0]} material={silverFrameMat}>
          <boxGeometry args={[postSize, postSize, totalD]} />
        </mesh>
        <mesh position={[halfTW - postSize * 0.5, loadPortDeckY + postSize * 0.5, 0]} material={silverFrameMat}>
          <boxGeometry args={[postSize, postSize, totalD]} />
        </mesh>
        {/* Center Bottom Cross-Tie Divider */}
        <mesh position={[0, loadPortDeckY + postSize * 0.5, 0]} material={silverFrameMat}>
          <boxGeometry args={[postSize, postSize, totalD]} />
        </mesh>

        {/* Top Crown Perimeter Bezel (at Y = TRACK_Y) */}
        <mesh position={[0, TRACK_Y - postSize * 0.5, -halfTD + postSize * 0.5]} material={silverFrameMat}>
          <boxGeometry args={[totalW, postSize, postSize]} />
        </mesh>
        <mesh position={[0, TRACK_Y - postSize * 0.5, halfTD - postSize * 0.5]} material={silverFrameMat}>
          <boxGeometry args={[totalW, postSize, postSize]} />
        </mesh>
        <mesh position={[-halfTW + postSize * 0.5, TRACK_Y - postSize * 0.5, 0]} material={silverFrameMat}>
          <boxGeometry args={[postSize, postSize, totalD]} />
        </mesh>
        <mesh position={[halfTW - postSize * 0.5, TRACK_Y - postSize * 0.5, 0]} material={silverFrameMat}>
          <boxGeometry args={[postSize, postSize, totalD]} />
        </mesh>
        {/* Center Top Crown Cross-Tie Divider */}
        <mesh position={[0, TRACK_Y - postSize * 0.5, 0]} material={silverFrameMat}>
          <boxGeometry args={[postSize, postSize, totalD]} />
        </mesh>

        {/* Direct Precision Clamping Collars to Overhead Track Rail */}
        {[-halfTW * 0.55, halfTW * 0.55].map((cx, cIdx) => (
          <group key={`track-cantilever-girder-${cIdx}`} position={[cx, TRACK_Y - postSize * 0.5, -halfTD]}>
            <mesh material={mats.darkMetal} castShadow>
              <boxGeometry args={[0.38, 0.36, 0.28]} />
            </mesh>
            {/* Chrome Tension Bolts */}
            {[-0.12, 0.12].map((bx) => (
              <mesh key={`clamp-bolt-${bx}`} position={[bx, 0.16, 0]}>
                <cylinderGeometry args={[0.035, 0.035, 0.1, 12]} />
                <meshStandardMaterial color="#F8FAFC" metalness={0.95} roughness={0.1} />
              </mesh>
            ))}
          </group>
        ))}

        {/* Light Black Transparent Glass Paneling (Left, Right, Rear) */}
        {[-1, 1].map((sx) => (
          <mesh
            key={`stocker-glass-x-${sx}`}
            position={[sx * (halfTW - 0.02), shaftMidY, 0]}
            material={smokedGlassMat}
          >
            <boxGeometry args={[0.04, shaftH - postSize * 2, totalD - postSize * 2]} />
          </mesh>
        ))}
        {/* Rear Glass Panels Split by Center Column */}
        {[-1, 1].map((sx) => (
          <mesh
            key={`stocker-glass-rear-${sx}`}
            position={[sx * (halfTW * 0.5 - postSize * 0.25), shaftMidY, halfTD - 0.02]}
            material={smokedGlassMat}
          >
            <boxGeometry args={[halfTW - postSize * 1.5, shaftH - postSize * 2, 0.04]} />
          </mesh>
        ))}
      </group>

      {/* ── 2. INDIVIDUAL LOAD PORT CARRIAGES & TOP TRANSFER TRAYS ── */}
      {ports.map(({ kind, x, z }) => {
        const hasFoupOnDeck = Boolean(occupiedPorts[kind]);
        const isPortTransferring = isTransferring && activePortKind === kind;
        const portLifterZ = z + lifterSideShift;

        return (
          <group key={kind} name={`LoadPortStation-${kind}`}>
            {/* Base Support Pedestal Under Load Port */}
            <BeveledPanel
              size={[nestW, padH, nestD]}
              material={mats.brushedMetal}
              position={[x, loadPortDeckY - padH * 0.5, portLifterZ]}
              radius={r * 0.35}
              smoothness={3}
              castShadow
              receiveShadow
            />
            <BeveledPanel
              size={[nestW * 0.98, footH, nestD * 0.72]}
              material={mats.darkMetal}
              position={[x, footH * 0.5, portLifterZ - nestD * 0.08]}
              radius={r * 0.2}
              receiveShadow
            />

            {/* High-Fidelity AMHS Vertical Lifter Carriage & Transfer Tray (Side of track) */}
            <FabAmhsLifter3D
              id={`StockerLifter-${kind}`}
              position={[x, 0, portLifterZ]}
              bottomY={loadPortDeckY}
              topTrackY={TRACK_Y}
              width={nestW}
              depth={nestD}
              extendDistance={lifterSideShift}
              extendDirection={-1}
              isTransferring={isPortTransferring}
              progress01={transferProgress01}
              transferOperation={transferOperation}
              foupPresent={
                isPortTransferring
                  ? lifterFoupPresent || hasFoupOnDeck
                  : hasFoupOnDeck
              }
              active={hasFoupOnDeck || isPortTransferring}
              status="RUNNING"
              opacity={opacity}
              showOuterFrame={false}
              showTrackClamp={true}
              showFoup={true}
            />
          </group>
        );
      })}
    </group>
  );
};

export default StockerExternalLoadPort;
